import React from 'react';
import { generatePath } from 'react-router-dom';
import Button from '@mui/material/Button';
import FundraiserPageLink from '@/components/campaign/FundraiserPageLink';
import CampaignLink from '@/components/common/CampaignLink';
import { canHaveEarnedRevenue } from '@/components/common/contribution/contributionFormUtils';
import TicketCostBreakdown from '@/components/common/contribution/TicketCostBreakdown';
import LabelChips from '@/components/common/LabelChips';
import RouterLink from '@/components/common/RouterLink';
import ContributionType from '@/constants/ContributionType';
import DedicationType from '@/constants/DedicationType';
import DonationExtSources from '@/constants/DonationExtSources';
import DonationExtSourcesNames from '@/constants/DonationExtSourcesNames';
import LabelResourceTypes from '@/constants/labels/LabelResourceTypes';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import {
  DONATION_PAYMENT_METHODS,
  DONATION_RECURRING_CADENCES,
  DONATION_TIME_TYPE_CONFIG,
  DONATION_TYPES,
  DONATION_VALUE_OF_TIME_CONFIG,
  DONATION_VOLUNTEER_CONFIG,
} from '@/lib/configs/contributions';
import { CUSTOM_DATE_FORMAT } from '@/lib/constants/common';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useEntityDetails } from '@/modules/EntityDetails';
import { formatCurrency } from '@/utils/currency';
import { formatDate } from '@/utils/dates';
import formatNumber from '@/utils/formatNumber';
import type { ContentItemType } from '../types/entityDetails';
import ContentBody from './ContentBody';
import ContentDetails from './ContentDetails';
import PledgeReconciled from './PledgeReconciled';

const BUTTON_CLASSES = {
  root: 'text-tertiary-main p-0',
  label: 'p-0',
};

export default function ContributionContent({
  donation,
  labels,
  pledgeDonation = null,
  softCredits = null,
  tickets,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ts-migrate)
any) {
  const { openDonationEntity } = useEntityDetails();

  const {
    actualValue,
    campaignDisplayName,
    campaignId,
    constituents,
    dedication,
    developers,
    donationDateString,
    donationPlan,
    donationTypeEnum,
    earnedRevenue,
    estimatedValue,
    fees,
    fundraiserPage,
    grantData,
    inKind,
    notes,
    paymentTypeEnum,
    pledgeData,
    restrictions,
    taxes,
    ticketFmv,
    ticketId,
    ticketQuantity,
    ticketedEventId,
    ticketedEventName,
    timeData,
  } = donation;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ts-migrate)
  const ticket = tickets?.find((t: any) => t.ticketId === ticketId);

  // common props
  // @ts-expect-error TS(7053) TODO(ts-migrate): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  const type = DONATION_TYPES[donationTypeEnum.toUpperCase()];
  const paymentType = DONATION_PAYMENT_METHODS.find(
    ({ value }) => value === paymentTypeEnum,
  );
  const note = notes?.length ? notes[0].text : '';
  // time contribution related props
  const timeValueProps = DONATION_VALUE_OF_TIME_CONFIG.find(
    ({ value }) => value === timeData?.timeValue,
  );
  const timeCategoryProps = DONATION_VOLUNTEER_CONFIG.find(
    ({ value }) => value === timeData?.timeCategory,
  );
  const timeUnitProps = DONATION_TIME_TYPE_CONFIG.find(
    ({ value }) => value === timeData?.timeUnit,
  );
  // in kind contribution related props
  const inKindData = inKind?.length && inKind[0];
  // restrictions contribution related props
  const restrictionArea =
    restrictions?.length && restrictions[0]?.restrictionDescription;
  // recurring contribution related props
  const recurringCadenceProps = DONATION_RECURRING_CADENCES.find(
    ({ value }) => value === donationPlan?.recurringCadenceEnum,
  );
  const recurringFormattedDate = formatDate(
    donationPlan?.recurringStartDateString,
    CUSTOM_DATE_FORMAT,
  );

  const earnedRevenueItems =
    earnedRevenue && canHaveEarnedRevenue(donationTypeEnum)
      ? [
          {
            title: translate('DONATION_AMOUNT'),
            value: formatCurrency(
              (donationTypeEnum === ContributionType.GENERAL
                ? actualValue
                : estimatedValue) - earnedRevenue,
              true,
            ),
          },
          {
            title: translate('EARNED_REVENUE'),
            value: formatCurrency(earnedRevenue, true),
          },
        ]
      : [];

  let contentItems: ContentItemType[] = [
    {
      title: translate('DONATION_CAMPAIGN'),
      value: campaignId && (
        <CampaignLink
          campaignDisplayName={campaignDisplayName}
          campaignId={campaignId}
        />
      ),
      className: 'col-span-full',
    },
    {
      title: translate('DONATION_FUNDRAISING_PAGE'),
      value: fundraiserPage && <FundraiserPageLink href={fundraiserPage} />,
      className: 'col-span-full',
    },
    {
      title: translate('CONTRIBUTION_TYPE_LABEL'),
      value: type.displayText,
      icon: type.icon,
    },
    {
      title: translate('CONTRIBUTION_DATE_LABEL'),
      value: formatDate(donationDateString, CUSTOM_DATE_FORMAT),
    },
  ];

  if (ticketedEventId) {
    contentItems = [
      ...contentItems.slice(0, 1),
      {
        title: translate('CAMPAIGN_EVENT'),
        value: ticketedEventId && (
          <RouterLink
            to={generatePath(AuthRoutes.CAMPAIGN_EVENT, {
              campaignId,
              eventId: ticketedEventId,
            })}
          >
            {ticketedEventName}
          </RouterLink>
        ),
        className: 'col-span-full',
      },
      ...contentItems.splice(1),
    ];
  }

  const paymentMethod = {
    title: translate('DONATION_PAYMENT_LABEL'),
    value: paymentType?.text,
    icon: paymentType?.icon,
  };
  const totalContributions = (ticketQuantity || 1) * actualValue;
  const totalDonations =
    totalContributions - (ticketFmv || 0) - (fees || 0) - (taxes || 0);

  switch (donationTypeEnum) {
    case ContributionType.GENERAL:
      contentItems.push(
        paymentMethod,
        {
          title: translate('CONTRIBUTION_AMOUNT_LABEL'),
          value: formatCurrency(actualValue, true),
        },
        ...earnedRevenueItems,
      );
      break;
    case ContributionType.PLEDGE:
      contentItems.push(
        paymentMethod,
        {
          title: translate('PLEDGE_AMOUNT_LABEL'),
          value: formatCurrency(estimatedValue, true),
        },
        {
          title: translate('PLEDGE_DUE_DATE_LABEL'),
          value: formatDate(pledgeData.pledgeDueDateString, CUSTOM_DATE_FORMAT),
        },
      );
      break;
    case ContributionType.IN_KIND:
      contentItems.push(
        {
          title: translate('IN_KIND_ITEM_CONTRIBUTED_LABEL'),
          value: inKindData?.inKindItemDescription,
        },
        {
          title: translate('IN_KIND_ITEM_VALUE_LABEL'),
          value: formatCurrency(inKindData.inKindTotalValue, true),
        },
        {
          title: translate('IN_KIND_ITEM_QUANTITY_LABEL'),
          value: formatNumber(inKindData.inKindItemQuantity),
        },
      );
      break;
    case ContributionType.RESTRICTED:
      contentItems.push(
        paymentMethod,
        {
          title: translate('CONTRIBUTION_AMOUNT_LABEL'),
          value: formatCurrency(estimatedValue, true),
        },
        ...earnedRevenueItems,
        {
          title: translate('RESTRICTION_AREA_LABEL'),
          value: restrictionArea,
        },
      );
      break;
    case ContributionType.TIME:
      contentItems.push(
        {
          title: translate('DONATION_TIME_VALUE_LABEL'),
          value: timeValueProps?.text,
          icon: timeValueProps?.icon,
        },
        {
          title: translate('DONATION_VOLUNTEER_LABEL'),
          value: timeCategoryProps?.text,
          icon: timeCategoryProps?.icon,
        },
        {
          title: translate('DONATION_TIME_AMOUNT'),
          // @ts-expect-error TS(2532) TODO(ts-migrate): Object is possibly 'undefined'.
          value: `${timeData.time} ${timeUnitProps.text}`,
        },
      );
      break;
    case ContributionType.RECURRING:
      contentItems.push(
        paymentMethod,
        {
          title: translate('CONTRIBUTION_AMOUNT_LABEL'),
          value: formatCurrency(estimatedValue, true),
        },
        ...earnedRevenueItems,
        {
          title: translate('DONATION_START_DATE_CADENCE'),
          value:
            recurringFormattedDate &&
            recurringCadenceProps &&
            `${recurringFormattedDate} - ${recurringCadenceProps?.text}`,
        },
      );
      break;
    case ContributionType.PLANNED_GIVING:
      contentItems.push({
        title: translate('CONTRIBUTION_AMOUNT_LABEL'),
        value: formatCurrency(estimatedValue, true),
      });
      break;
    case ContributionType.GRANT:
      contentItems.push(
        {
          title: translate('GRANT_NAME_LABEL'),
          value: grantData.grantName,
          className: 'col-span-full',
        },
        paymentMethod,
        {
          title: translate('GRANT_AMOUNT_LABEL'),
          value: formatCurrency(estimatedValue, true),
        },
        {
          title: translate('CONDITIONS_OF_RESTRICTION_LABEL'),
          value: grantData.restrictionCondition,
          className: 'col-span-full',
        },
      );
      break;
    case ContributionType.TICKET:
      if (ticket) {
        contentItems.push({
          title: translate('CAMPAIGN_EVENT_TICKET_OPTION'),
          value: ticket.name,
        });
      }
      if (ticketQuantity) {
        contentItems.push({
          title: translate('CAMPAIGN_EVENT_TICKET_QTY'),
          value: ticketQuantity,
        });
      }
      contentItems.push(
        paymentMethod,
        {
          title: translate('CONTRIBUTION_AMOUNT_LABEL'),
          value: formatCurrency(actualValue, true),
        },
        {
          title: translate('CAMPAIGN_EVENT_TICKET_FMV'),
          value: formatCurrency(ticketFmv, true),
        },
        {
          title: translate('CAMPAIGN_EVENT_DONATION_AMOUNT'),
          value: formatCurrency(totalDonations, true),
        },
      );
      break;
    case ContributionType.SPONSORSHIP:
      contentItems.push(paymentMethod, {
        title: translate('CONTRIBUTION_AMOUNT_LABEL'),
        value: formatCurrency(actualValue, true),
      });
      break;
    default:
      break;
  }

  if (donation.extDonationId) {
    contentItems.push({
      title: translate('REPORTS_CONTRIBUTION_SOURCE'),
      value:
        donation.extSource === DonationExtSources.STRIPE_CUSTOMER ? (
          <a
            href={`https://dashboard.stripe.com/payments/${donation.extDonationId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-tertiary-main"
          >
            {
              DonationExtSourcesNames[
                donation.extSource as keyof typeof DonationExtSourcesNames
              ]
            }
          </a>
        ) : (
          DonationExtSourcesNames[
            donation.extSource as keyof typeof DonationExtSourcesNames
          ]
        ),
    });
  }

  if (softCredits?.length) {
    contentItems.push({
      title: translate('SOFT_CREDIT'),
      value: softCredits.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ts-migrate)
        ({ constituentId, displayName }: any, i: any, arr: any) => (
          <React.Fragment key={constituentId}>
            <RouterLink
              to={generatePath(AuthRoutes.CONSTITUENT, { constituentId })}
            >
              {displayName}
            </RouterLink>
            {i + 1 < arr.length ? ', ' : ''}
          </React.Fragment>
        ),
      ),
    });
  }

  if (dedication?.dedicatedTo) {
    contentItems.push({
      className: 'col-span-full',
      title: translate('DEDICATION'),
      value: translate('DEDICATION_ATTRIBUTION', {
        type: translate(
          dedication.type === DedicationType.IN_HONOR
            ? 'DEDICATION_IN_HONOR'
            : 'DEDICATION_IN_MEMORY',
        ),
        attribution: dedication.constituentId ? (
          <RouterLink
            to={generatePath(AuthRoutes.CONSTITUENT, {
              constituentId: dedication.constituentId,
            })}
          >
            {dedication.dedicatedTo}
          </RouterLink>
        ) : (
          dedication.dedicatedTo
        ),
      }),
    });
  }

  contentItems.push(
    {
      title: translate('DONATION_NOTES_LABEL'),
      value: note,
      className: 'col-span-full',
    },
    {
      key: 'labels',
      value: <LabelChips labels={labels} resource={LabelResourceTypes.DON} />,
      className: 'col-span-full',
    },
  );

  const { constituentId } = constituents[0];

  return (
    <ContentBody entityId={donation.donationId}>
      {donationTypeEnum === ContributionType.PLEDGE && (
        <PledgeReconciled
          pledgeData={pledgeData}
          constituentId={constituentId}
          donationId={donation.donationId}
          displayName={constituents[0].displayName}
        />
      )}
      {donationTypeEnum !== ContributionType.PLEDGE && pledgeDonation && (
        <div
          className="mb-4 rounded bg-grey-100 py-3 text-center"
          data-testid="donation-linked-to-pledge"
        >
          {translate('CONTRIBUTION_LINKED_TO_A_PLEDGE', {
            amount: formatCurrency(pledgeDonation.amount, true),
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any, react/no-unstable-nested-components -- TODO(ts-migrate), TODO(ts-migrate) */
            a: (chunks: any) => (
              <Button
                classes={BUTTON_CLASSES}
                onClick={() =>
                  // ESLint won't stop complaining about this even though proptype is declared
                  // eslint-disable-next-line react/prop-types
                  openDonationEntity(constituentId, pledgeDonation.donationId)
                }
              >
                {chunks}
              </Button>
            ),
          })}
        </div>
      )}
      <ContentDetails
        assignees={developers}
        assigneeTitle={translate('ASSOCIATED_TEAM_MEMBERS_LABEL')}
        contentItems={contentItems}
        title={translate('FEED_SLIDER_DETAILS_TITLE')}
      />
      {!!ticketedEventId && (
        <div className="mb-4">
          <TicketCostBreakdown
            fmv={ticketFmv}
            fees={fees}
            taxes={taxes}
            totalContributions={totalContributions}
            totalDonations={totalDonations}
          />
        </div>
      )}
    </ContentBody>
  );
}
